import React from 'react'
import i18next, {t} from 'i18next'
import {Col, Row} from 'antd'
import {isEmpty} from 'dna-common'
import moment from 'moment/moment'
import {getAmountWithCurrency} from '@/utils'
import {
    CardSchemeLabelWithIcon,
    DateTimeLabel,
    GeolocationMap,
    PaymentMethodOrMaskedCard,
    Status
} from '@/components'
import {
    Currencies,
    DATE_TIME_FORMAT,
    OnlinePaymentStatus,
    PaymentMethodType,
    VerificationStatus
} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {EpayPayment, SignifydDetails} from '@/api/transactions/models'
import {TagType} from '@/components/dumb/Tag/props'
import icons from '~/assets/icons'
import {ChildTransactionsDetails} from '../components'

export const getOnlinePaymentStatusName = (status: OnlinePaymentStatus): string => {
    return i18next.t(
        [`onlinePaymentStatus.${status}`, `onlinePaymentStatus.${OnlinePaymentStatus.other}`],
        {ns: 'enums'}
    )
}

export const getOnlinePaymentStatusTagType = (status: OnlinePaymentStatus): TagType => {
    switch (status) {
        case OnlinePaymentStatus.failed:
        case OnlinePaymentStatus.reject:
            return TagType.Error
        case OnlinePaymentStatus.processing:
        case OnlinePaymentStatus.threeDSecure:
            return TagType.Processing
        case OnlinePaymentStatus.auth:
        case OnlinePaymentStatus.charge:
        case OnlinePaymentStatus.verified:
        case OnlinePaymentStatus.tokenized:
            return TagType.Success
        default:
            return TagType.Default
    }
}

export const getOnlinePaymentStatusIconType = (status: OnlinePaymentStatus): keyof typeof icons => {
    switch (status) {
        case OnlinePaymentStatus.failed:
        case OnlinePaymentStatus.reject:
        case OnlinePaymentStatus.cancel:
            return 'close'
        case OnlinePaymentStatus.processing:
        case OnlinePaymentStatus.auth:
        case OnlinePaymentStatus.created:
        case OnlinePaymentStatus.threeDSecure:
        case OnlinePaymentStatus.new:
            return 'clock'
        case OnlinePaymentStatus.charge:
        case OnlinePaymentStatus.verified:
        case OnlinePaymentStatus.tokenized:
            return 'tick'
        case OnlinePaymentStatus.refund:
            return 'restore'
        case OnlinePaymentStatus.credited:
            return 'arrow_right'
        case OnlinePaymentStatus.abandoned:
            return 'eye_closed'
        default:
            return 'info_help'
    }
}

export const getIconType = (type: TagType) => {
    switch (type) {
        case TagType.Success:
            return 'tick'
        case TagType.Warn:
            return 'warn'
        default:
            return 'close'
    }
}

export const getVerificationStatusTagType = (status: VerificationStatus): TagType => {
    switch (status) {
        case VerificationStatus.matched:
        case VerificationStatus.yes:
        case VerificationStatus.enrolled:
        case VerificationStatus.challengeRequired:
        case VerificationStatus.attempted:
        case VerificationStatus.authenticated:
            return TagType.Success
        case VerificationStatus.partialMatch:
            return TagType.Warn
        case VerificationStatus.notChecked:
        case VerificationStatus.notSet:
        case VerificationStatus.notAttempted:
            return TagType.Default
        default:
            return TagType.Error
    }
}

export const getPaEnrollmentStatus = (status: string): VerificationStatus => {
    switch (status) {
        case 'Y':
            return VerificationStatus.enrolled
        case 'C':
            return VerificationStatus.challengeRequired
        case 'A':
            return VerificationStatus.attempted
        case 'N':
            return VerificationStatus.notEnrolled
        case 'U':
            return VerificationStatus.unableToCheck
        case 'R':
            return VerificationStatus.authRejected
        default:
            return VerificationStatus.notAttempted
    }
}

export const getPaAuthStatus = (status: string): VerificationStatus => {
    switch (status) {
        case 'Y':
            return VerificationStatus.authenticated
        case 'A':
            return VerificationStatus.attempted
        case 'N':
            return VerificationStatus.notAuthenticated
        case 'U':
            return VerificationStatus.unableToAuth
        case 'R':
            return VerificationStatus.authRejected
        default:
            return VerificationStatus.notAttempted
    }
}

const renderVerificationStatus = (status: string) => {
    return (
        <Row>
            <Col>
                <Status
                    status={i18next.t('OnlinePayments.statuses.' + status)}
                    type={getVerificationStatusTagType(status as VerificationStatus)}
                    iconType={getIconType(
                        getVerificationStatusTagType(status as VerificationStatus)
                    )}
                />
            </Col>
        </Row>
    )
}

export const getExportColumns = () => ({
    createdDate: {value: 'createdDate', label: i18next.t('OnlinePayments.createdDate')},
    store: {value: 'store', label: i18next.t('OnlinePayments.shop')},
    amount: {value: 'amount', label: i18next.t('OnlinePayments.amount')},
    payoutAmount: {value: 'payoutAmount', label: i18next.t('OnlinePayments.payoutAmount')},
    balance: {value: 'balance', label: i18next.t('OnlinePayments.balance')},
    currency: {value: 'currency', label: i18next.t('OnlinePayments.currency')},
    status: {value: 'status', label: i18next.t('OnlinePayments.status')},
    payoutDate: {value: 'payoutDate', label: i18next.t('OnlinePayments.payoutDate')},
    id: {value: 'id', label: i18next.t('OnlinePayments.id')},
    merchantReference: {value: 'merchantReference', label: i18next.t('OnlinePayments.invoiceId')},
    reference: {value: 'reference', label: i18next.t('OnlinePayments.reference')},
    transactionType: {
        value: 'transactionType',
        label: i18next.t('OnlinePayments.transactionTypeLower')
    },
    processingType: {value: 'processingType', label: i18next.t('OnlinePayments.processingType')},
    description: {value: 'description', label: i18next.t('OnlinePayments.description')},
    paymentMethod: {value: 'paymentMethod', label: i18next.t('OnlinePayments.paymentMethod')},
    issuer: {value: 'issuer', label: i18next.t('OnlinePayments.issuer')},
    cardScheme: {value: 'cardScheme', label: i18next.t('OnlinePayments.cardType')},
    cardMask: {value: 'cardMask', label: i18next.t('OnlinePayments.cardMask')},
    cardExpiryDate: {value: 'cardExpiryDate', label: i18next.t('OnlinePayments.cardExpiryDate')},
    secure3D: {value: 'secure3D', label: i18next.t('OnlinePayments.secure3D')},
    responseCode: {value: 'responseCode', label: i18next.t('OnlinePayments.acquirerResponseCode')},
    authCode: {value: 'authCode', label: i18next.t('OnlinePayments.authCode')},
    message: {value: 'message', label: i18next.t('OnlinePayments.message')},
    avsHouseNumberResult: {
        value: 'avsHouseNumberResult',
        label: i18next.t('OnlinePayments.avsHouseNumberResult')
    },
    avsPostcodeResult: {
        value: 'avsPostcodeResult',
        label: i18next.t('OnlinePayments.avsPostcodeResult')
    },
    cscResult: {value: 'cscResult', label: i18next.t('OnlinePayments.cscResult')},
    paEnrollment: {value: 'paEnrollment', label: i18next.t('OnlinePayments.paEnrollment')},
    paAuthentication: {
        value: 'paAuthentication',
        label: i18next.t('OnlinePayments.paAuthentication')
    },
    payerName: {value: 'payerName', label: i18next.t('OnlinePayments.payerName')},
    accountId: {value: 'accountId', label: i18next.t('OnlinePayments.accountId')},
    payerEmail: {value: 'payerEmail', label: i18next.t('OnlinePayments.payerEmail')},
    payerPhone: {value: 'payerPhone', label: i18next.t('OnlinePayments.payerPhone')},
    payerIp: {value: 'payerIp', label: i18next.t('OnlinePayments.payerIp')},
    ipCountry: {value: 'ipCountry', label: i18next.t('OnlinePayments.ipCountry')},
    ipCity: {value: 'ipCity', label: i18next.t('OnlinePayments.ipCity')},
    ipLatitude: {value: 'ipLatitude', label: i18next.t('OnlinePayments.ipLatitude')},
    ipLongitude: {value: 'ipLongitude', label: i18next.t('OnlinePayments.ipLongitude')},
    initiatorEmail: {value: 'initiatorEmail', label: i18next.t('OnlinePayments.initiatorEmail')},
    additionalInfo: {value: 'additionalInfo', label: i18next.t('OnlinePayments.additionalInfo')}
})

const payerDetails = (selectedTransaction) => ({
    title: i18next.t('OnlinePayments.payerDetails'),
    fields: [
        {
            label: i18next.t('OnlinePayments.payer'),
            value: selectedTransaction.payerName
        },
        {
            label: i18next.t('OnlinePayments.accountId'),
            value: selectedTransaction.accountId
        },
        {
            label: i18next.t('OnlinePayments.email'),
            value: selectedTransaction.payerEmail
        },
        {
            label: i18next.t('OnlinePayments.phone'),
            value: selectedTransaction.payerPhone
        }
    ]
})

const paymentPageDetails = (selectedTransaction) => ({
    title: i18next.t('OnlinePayments.paymentPage.title'),
    fields: [
        {
            label: i18next.t('OnlinePayments.paymentPage.language'),
            value: selectedTransaction.language
        },
        {
            label: i18next.t('OnlinePayments.paymentPage.postLinkAddress'),
            value: selectedTransaction.postLink
        },
        {
            label: i18next.t('OnlinePayments.paymentPage.postLink'),
            value: selectedTransaction.postLinkStatus
                ? i18next.t('OnlinePayments.paymentPage.ok')
                : i18next.t('OnlinePayments.paymentPage.failed')
        }
    ]
})

const renderGeolocationText = (payment: EpayPayment) => {
    const {ipCity, ipCountry, ipRegion, ipDistrict, ipLatitude, ipLongitude} = payment

    let locationSpecification = [ipCountry, ipRegion, ipDistrict]
        .filter((item) => !!item)
        .join(', ')

    if (locationSpecification.length) {
        locationSpecification = '(' + locationSpecification + ')'
    }

    return (
        <div>
            <div>
                {ipCity} {locationSpecification}
            </div>
            {ipLatitude && ipLongitude ? (
                <a
                    href={`http://www.google.com/maps/place/${ipLatitude},${ipLongitude}`}
                    target='blank'
                >
                    {`N${ipLatitude}, E${ipLongitude}`}
                </a>
            ) : null}
        </div>
    )
}

const renderGeolocationMap = (payment: EpayPayment) => {
    const {ipLatitude, ipLongitude} = payment

    return ipLatitude && ipLongitude ? (
        <GeolocationMap
            center={{
                lat: ipLatitude,
                lng: ipLongitude
            }}
        />
    ) : null
}

const geolocationDetails = (selectedTransaction) => ({
    title: i18next.t('OnlinePayments.geolocation.location'),
    fields: [
        {
            label: i18next.t('OnlinePayments.geolocation.payerIp'),
            value: selectedTransaction.payerIp
        },
        {
            label: i18next.t('OnlinePayments.description'),
            value: selectedTransaction.ipLongitude,
            render: () => renderGeolocationText(selectedTransaction)
        },
        selectedTransaction.ipLatitude &&
            selectedTransaction.ipLongitude && {
                value: selectedTransaction.ipLatitude,
                render: () => renderGeolocationMap(selectedTransaction)
            }
    ].filter((item) => item)
})

const orderDetails = (selectedTransaction) => ({
    title: i18next.t('OnlinePayments.paymentDetails'),
    fields: [
        {
            label: i18next.t('OnlinePayments.paymentMethod'),

            render: () => {
                const {cardType, cardMask, paymentMethod} = selectedTransaction
                return (
                    <PaymentMethodOrMaskedCard
                        cardScheme={cardType}
                        cardMask={cardMask}
                        paymentMethod={paymentMethod}
                    />
                )
            }
        },
        {
            label: i18next.t('OnlinePayments.amount'),
            value: getAmountWithCurrency(selectedTransaction.amount, selectedTransaction.currency)
        },
        selectedTransaction.status === OnlinePaymentStatus.charge && {
            label: i18next.t('OnlinePayments.chargedAmount'),
            value: getAmountWithCurrency(
                selectedTransaction.payoutAmount,
                selectedTransaction.currency
            )
        },
        selectedTransaction.status === OnlinePaymentStatus.refund && {
            label: i18next.t('OnlinePayments.refundAmount'),
            value: getAmountWithCurrency(
                selectedTransaction.payoutAmount,
                selectedTransaction.currency
            )
        },
        selectedTransaction.status === OnlinePaymentStatus.charge && {
            label: i18next.t('OnlinePayments.balance'),
            value: getAmountWithCurrency(selectedTransaction.balance, selectedTransaction.currency)
        },
        {
            label: i18next.t('OnlinePayments.status'),
            value: getOnlinePaymentStatusName(selectedTransaction?.status),
            render: () => (
                <Row>
                    <Col>
                        <Status
                            status={getOnlinePaymentStatusName(selectedTransaction?.status)}
                            type={getOnlinePaymentStatusTagType(selectedTransaction.status)}
                            iconType={getOnlinePaymentStatusIconType(selectedTransaction.status)}
                        />
                    </Col>
                </Row>
            )
        },
        {
            label: i18next.t('OnlinePayments.date'),
            value: selectedTransaction.createdDate,
            render: () => <DateTimeLabel date={selectedTransaction?.createdDate} />
        },
        {
            label: i18next.t('OnlinePayments.id'),
            value: selectedTransaction?.id,
            copyable: true
        },
        {
            label: i18next.t('OnlinePayments.orderNumber'),
            value: selectedTransaction.invoiceId,
            copyable: true
        },
        {
            label: i18next.t('OnlinePayments.reference'),
            value: selectedTransaction.reference,
            copyable: true
        },
        {
            label: i18next.t('OnlinePayments.transactionType'),
            value: i18next.t(
                `transactionTypes.${selectedTransaction?.transactionType?.toUpperCase()}`,
                {ns: 'enums'}
            )
        },
        selectedTransaction.initiatorEmail && {
            label: i18next.t('OnlinePayments.initiatorEmail'),
            value: selectedTransaction?.initiatorEmail,
            copyable: true
        }
    ].filter((item) => item)
})

const summaryDetails = (selectedTransaction: EpayPayment) => {
    const decodedValue = selectedTransaction?.data
        ? window.atob(selectedTransaction.data)
        : undefined

    let refundReasonValueTranslation = undefined

    if (i18next.exists(`refundReason.${decodedValue}`, {ns: 'enums', defaultValue: decodedValue})) {
        refundReasonValueTranslation = i18next.t(`refundReason.${decodedValue}`, {
            ns: 'enums',
            defaultValue: decodedValue
        })
    }

    const checkForRefundStatus = () => {
        return (
            selectedTransaction.status === OnlinePaymentStatus.refund ||
            selectedTransaction.status === OnlinePaymentStatus.credited
        )
    }

    return {
        title: i18next.t('OnlinePayments.summary'),
        fields: [
            selectedTransaction.processingTypeName && {
                label: i18next.t('OnlinePayments.processingTypeName'),
                value: selectedTransaction.processingTypeName
            },
            {
                label: i18next.t('OnlinePayments.shop'),
                value: selectedTransaction.shop
            },
            refundReasonValueTranslation &&
                checkForRefundStatus() && {
                    label: i18next.t('OnlinePayments.refundReason'),
                    value: refundReasonValueTranslation,
                    copyable: true
                },
            {
                label: i18next.t('OnlinePayments.description'),
                value: selectedTransaction.description
            },
            {
                label: i18next.t('OnlinePayments.authorizedOn'),
                value: `${selectedTransaction.authDate}`,
                render: () => <DateTimeLabel date={selectedTransaction.authDate} />
            },
            selectedTransaction.status === OnlinePaymentStatus.cancel && {
                label: i18next.t('OnlinePayments.cancelOn'),
                value: selectedTransaction.payoutDate,
                render: () => <DateTimeLabel date={selectedTransaction.payoutDate} />
            },
            selectedTransaction.status === OnlinePaymentStatus.charge && {
                label: i18next.t('OnlinePayments.chargedOn'),
                value: selectedTransaction.payoutDate,
                render: () => <DateTimeLabel date={selectedTransaction.payoutDate} />
            },
            selectedTransaction.status === OnlinePaymentStatus.refund && {
                label: i18next.t('OnlinePayments.refundedOn'),
                value: selectedTransaction.payoutDate,
                render: () => <DateTimeLabel date={selectedTransaction.payoutDate} />
            },
            !isEmpty(selectedTransaction.issuer) && {
                label: i18next.t('OnlinePayments.issuer'),
                value: selectedTransaction.issuer
            },
            !isEmpty(selectedTransaction.paymentMethod) && {
                label: i18next.t('OnlinePayments.card'),
                value: selectedTransaction.cardMask,
                render: () => (
                    <CardSchemeLabelWithIcon
                        cardScheme={selectedTransaction.cardType}
                        title={selectedTransaction.cardMask}
                    />
                )
            },
            selectedTransaction.donationAmount && {
                label: i18next.t('OnlinePayments.donationAmount'),
                value: getAmountWithCurrency(
                    selectedTransaction.donationAmount,
                    selectedTransaction.currency
                )
            },
            selectedTransaction.cardExpiryDate &&
                /^\d{1,2}\/\d{2}$/.test(selectedTransaction.cardExpiryDate) && {
                    label: i18next.t('OnlinePayments.cardExpiryDate'),
                    value: selectedTransaction.cardExpiryDate
                },
            selectedTransaction.acquirerResponseCode && {
                label: i18next.t('OnlinePayments.acquirerResponseCode'),
                value: selectedTransaction.acquirerResponseCode
            },
            selectedTransaction.authCode && {
                label: i18next.t('OnlinePayments.authCode'),
                value: selectedTransaction.authCode
            },
            selectedTransaction.resultMessage &&
                selectedTransaction.status === OnlinePaymentStatus.reject && {
                    label: i18next.t('OnlinePayments.resultMessage'),
                    value: selectedTransaction.resultMessage
                },
            selectedTransaction.avsResult && {
                label: i18next.t('OnlinePayments.avsResult'),
                value: selectedTransaction.avsResult
            }
        ].filter((item) => item)
    }
}

const verificationDetails = (selectedTransaction: EpayPayment) => {
    return {
        title: i18next.t('OnlinePayments.verifications'),
        fields: [
            {
                label: i18next.t('OnlinePayments.secure3D'),
                value: selectedTransaction.secure3D,
                render: () =>
                    renderVerificationStatus(
                        selectedTransaction.secure3D
                            ? VerificationStatus.yes
                            : VerificationStatus.no
                    )
            },
            selectedTransaction.avsHouseNumberResult && {
                label: i18next.t('OnlinePayments.AVSHouseNumberResult'),
                value: i18next.t(
                    'OnlinePayments.statuses.' + selectedTransaction.avsHouseNumberResult
                ),
                render: () => renderVerificationStatus(selectedTransaction.avsHouseNumberResult)
            },
            selectedTransaction.avsPostcodeResult && {
                label: i18next.t('OnlinePayments.AVSPostcodeResult'),
                value: selectedTransaction.avsPostcodeResult,
                render: () => renderVerificationStatus(selectedTransaction.avsPostcodeResult)
            },
            selectedTransaction.cscResult && {
                label: i18next.t('OnlinePayments.cscResult'),
                value: selectedTransaction.cscResult,
                render: () => renderVerificationStatus(selectedTransaction.cscResult)
            },
            Boolean(selectedTransaction.paEnrollment) && {
                label: i18next.t('OnlinePayments.paEnrollment'),
                value: selectedTransaction.paEnrollment,
                render: () =>
                    renderVerificationStatus(
                        getPaEnrollmentStatus(selectedTransaction.paEnrollment)
                    )
            },
            Boolean(selectedTransaction.paAuthentication) && {
                label: i18next.t('OnlinePayments.paAuthentication'),
                value: selectedTransaction.paAuthentication,
                render: () =>
                    renderVerificationStatus(getPaAuthStatus(selectedTransaction.paAuthentication))
            }
        ].filter((item) => item)
    }
}

export const originalTransactionDetails = (selectedTransaction: EpayPayment) => {
    const {parentTransaction} = selectedTransaction
    const transaction = parentTransaction

    return {
        title: i18next.t('OnlinePayments.originalTransactionDetails'),
        fields: [
            {
                label: i18next.t('OnlinePayments.transactionDate'),
                value: moment(transaction.createdDate).format(DATE_TIME_FORMAT)
            },
            {
                label: i18next.t('OnlinePayments.amount'),
                value: getAmountWithCurrency(
                    transaction.amount,
                    transaction.currency || DEFAULT_CURRENCY
                )
            },
            {
                label: i18next.t('OnlinePayments.balance'),
                value: getAmountWithCurrency(
                    transaction.balance,
                    transaction.currency || DEFAULT_CURRENCY
                )
            },
            {
                label: i18next.t('OnlinePayments.status'),
                value: getOnlinePaymentStatusName(transaction.status)
            },
            {
                label: i18next.t('OnlinePayments.id'),
                value: transaction.id
            },
            {
                label: i18next.t('OnlinePayments.orderNumber'),
                value: transaction.invoiceId
            },
            {
                label: i18next.t('OnlinePayments.transactionType'),
                value: i18next.t(
                    `transactionTypes.${parentTransaction?.transactionType?.toUpperCase()}`,
                    {ns: 'enums'}
                )
            }
        ]
    }
}

export const childrenTransactionsDetails = (selectedTransaction: EpayPayment) => {
    const {childrenTransactions} = selectedTransaction

    if (!childrenTransactions || childrenTransactions.length === 0) {
        return null
    }

    return (
        childrenTransactions.length && {
            title: i18next.t('OnlinePayments.relatedTransactions'),
            fields: [
                {
                    title: null,
                    label: null,
                    render: () => <ChildTransactionsDetails details={childrenTransactions} />
                }
            ]
        }
    )
}

const payPalDetails = (selectedTransaction: EpayPayment) => {
    return {
        title: i18next.t('OnlinePayments.payPal.details'),
        fields: [
            selectedTransaction.additionalDetails?.paypalOrderStatus && {
                label: i18next.t('OnlinePayments.payPal.paypalOrderStatus'),
                value: selectedTransaction.additionalDetails?.paypalOrderStatus
            },
            selectedTransaction.additionalDetails?.paypalCaptureStatus && {
                label: i18next.t('OnlinePayments.payPal.paypalCaptureStatus'),
                value: selectedTransaction.additionalDetails?.paypalCaptureStatus
            },
            selectedTransaction.additionalDetails?.paypalCaptureStatusReason && {
                label: i18next.t('OnlinePayments.payPal.paypalCaptureStatusReason'),
                value: selectedTransaction.additionalDetails?.paypalCaptureStatusReason
            },
            selectedTransaction.additionalDetails?.paypalSellerProtectionStatus && {
                label: i18next.t('OnlinePayments.payPal.paypalSellerProtectionStatus'),
                value: selectedTransaction.additionalDetails?.paypalSellerProtectionStatus
            },
            selectedTransaction.additionalDetails?.paypalSellerProtectionDisputeCategories && {
                label: i18next.t('OnlinePayments.payPal.paypalSellerProtectionDisputeCategories'),
                value: selectedTransaction.additionalDetails
                    ?.paypalSellerProtectionDisputeCategories
            },
            selectedTransaction.additionalDetails?.paypalPayerName && {
                label: i18next.t('OnlinePayments.payPal.paypalPayerName'),
                value: selectedTransaction.additionalDetails?.paypalPayerName
            },
            selectedTransaction.additionalDetails?.paypalEmail && {
                label: i18next.t('OnlinePayments.payPal.paypalEmail'),
                value: selectedTransaction.additionalDetails?.paypalEmail
            },
            selectedTransaction.additionalDetails?.paypalPayerID && {
                label: i18next.t('OnlinePayments.payPal.paypalPayerID'),
                value: selectedTransaction.additionalDetails?.paypalPayerID
            },
            selectedTransaction.additionalDetails?.paypalPayerCountryCode && {
                label: i18next.t('OnlinePayments.payPal.paypalPayerCountryCode'),
                value: selectedTransaction.additionalDetails?.paypalPayerCountryCode
            }
        ].filter((item) => item)
    }
}

const signifydDetails = (signifydDetails: SignifydDetails) => ({
    title: i18next.t('OnlinePayments.signifydDetails'),
    fields: [
        {
            label: i18next.t('OnlinePayments.signifydId'),
            value: signifydDetails.signifydId,
            copyable: true
        },
        {
            label: i18next.t('OnlinePayments.decision'),
            value: signifydDetails.decision
        },
        {
            label: i18next.t('OnlinePayments.reason'),
            value: signifydDetails.reason
        },
        {
            label: i18next.t('OnlinePayments.score'),
            value: signifydDetails.score
        }
    ]
})

const transactionDetails = (selectedTransaction: EpayPayment) => {
    const details = [
        orderDetails(selectedTransaction),
        selectedTransaction?.parentTransaction && originalTransactionDetails(selectedTransaction),
        selectedTransaction?.childrenTransactions?.length &&
            childrenTransactionsDetails(selectedTransaction),
        verificationDetails(selectedTransaction),
        summaryDetails(selectedTransaction),
        selectedTransaction.paymentMethod === PaymentMethodType.paypal &&
            Boolean(selectedTransaction.additionalDetails) &&
            payPalDetails(selectedTransaction),
        payerDetails(selectedTransaction),
        geolocationDetails(selectedTransaction),
        paymentPageDetails(selectedTransaction)
    ].filter((item) => item)

    if (selectedTransaction.signifydDetails) {
        details.push(signifydDetails(selectedTransaction.signifydDetails))
    }

    return details
}

export const getTransactionDetails = (selectedTransaction) => {
    if (!selectedTransaction?.status) {
        return []
    }

    return transactionDetails(selectedTransaction)
}

const enum ProcessTypeId {
    InitialUCOF = 1,
    UCOF_MIT = 2,
    InitialCOF = 3,
    COF_CIT = 4,
    MailOrder = 5,
    TelephoneOrder = 6,
    InitialRecurring = 7,
    RecurringMIT = 8,
    MailOrderInitialCOF = 9,
    TelephoneOrderInitialCOF = 10,
    MOTORecurringMIT = 11
}

const validProcessingTypeIdsForProcessNewPayment = [
    ProcessTypeId.InitialUCOF,
    ProcessTypeId.InitialCOF,
    ProcessTypeId.InitialRecurring,
    ProcessTypeId.MailOrderInitialCOF,
    ProcessTypeId.TelephoneOrderInitialCOF,
    ProcessTypeId.MOTORecurringMIT
]

export const isProcessNewPaymentActionVisible = (
    processingTypeId: number,
    status: OnlinePaymentStatus,
    paymentMethod: PaymentMethodType
) => {
    return (
        validProcessingTypeIdsForProcessNewPayment.includes(processingTypeId) &&
        ![
            OnlinePaymentStatus.reject,
            OnlinePaymentStatus.failed,
            OnlinePaymentStatus.processing,
            OnlinePaymentStatus.new
        ].includes(status) &&
        [
            PaymentMethodType.card,
            PaymentMethodType.applepay,
            PaymentMethodType.googlepay,
            PaymentMethodType.clicktopay
        ].includes(paymentMethod)
    )
}
